/* @flow */

import React, {useCallback} from 'react';
import MessageBox from '../../MessageBox/MessageBox';
import {error} from '../../../../../components/ErrorToast/ErrorToast';
import ThorViewScss from './ThorView.scss';
import DownloadUtil from '../../../../../utils/DownloadUtil';
import AppsUtil from '../../../../../utils/AppsUtil';
import Url from '../../../../../utils/Url';
import {getHeadingTextId, getBodyTextId, WORKFLOWS} from './../stringUtil';
import {DOWNLOAD_CONTROLLER_USER_SOURCE, LOGGED_OUT_CAMPAIGN_WORKFLOWS} from '../../../../../constants/AppPicker/appPickerConstants';
import CCDUtil from '../../../../../utils/CCDUtil';

type Props = {
	className: ?string,
	source: string,
	displayCoachMark: Function,
	handleDownloadEvent: Function,
	handleAnalyticsEvent: Function,
	onGetHelp: Function,
	product: Object,
	intl: Object,
	messagesList: Array<Object>
};

export default ({
	className = '',
	product,
	intl,
	messagesList,
	source,
	handleAnalyticsEvent,
	handleDownloadEvent,
	displayCoachMark,
	onGetHelp,
}: Props) => {
	const {formatMessage} = intl;
	const {sapCode: sapcode} = product;
	const isLoggedOutCampaignExp = (source === DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE);
	const isOpenFileWorkflow = Url.getParam('workflow') === LOGGED_OUT_CAMPAIGN_WORKFLOWS.OPEN_FILE && Url.getParam('file');

	const onRestartDownload = useCallback((e) => {
		e && e.preventDefault();
		DownloadUtil.downloadProduct({sapcode, hasThor: false})
			.then((workflow) => {
				handleAnalyticsEvent({
					eventObj: {
						type:'click',
						subtype:'reinstall-ccd-and-app'
					},
					updatedWorkflow: workflow
				});
				handleDownloadEvent(false, true);
			});
		const popUpInstaller = document.querySelector('#popUpInstaller');
		const isRestart = !(popUpInstaller === null || popUpInstaller.style.display === 'none');
		displayCoachMark(isRestart, () => {});
	}, [displayCoachMark, sapcode, handleAnalyticsEvent, handleDownloadEvent]);

	const onRetry = useCallback((e) => {
		e && e.preventDefault();
		DownloadUtil.downloadProduct({sapcode, hasThor: true, downloadDelay: 500})
			.then(() => {
				handleDownloadEvent(true);
			});
		handleAnalyticsEvent({
			eventObj: {
				type:'click',
				subtype: 'restart-download'
			},
			contentObj: {
				category: 'Link'
			}
		});
	}, [sapcode, handleAnalyticsEvent, handleDownloadEvent]);

	const onRouteRetry = useCallback((e) => {
		e && e.preventDefault();
		if (CCDUtil.isRouteToPathCampaign()) {
			DownloadUtil.routeToCCDPath(Url.getParam('path'));
		}
		if (CCDUtil.isProductRouteToPathCampaign(sapcode)) {
			DownloadUtil.routeToProductPath(Url.getParam('path'));
		}
		handleAnalyticsEvent({
			eventObj: {
				type:'click',
				subtype: 'try-again'
			},
			contentObj: {
				category: 'Link',
				action: Url.getParam('path')
			}
		});
	}, [handleAnalyticsEvent, sapcode]);

	const onOpenFile = useCallback((e) => {
		e && e.preventDefault();
		const isSuccess = DownloadUtil.downloadFile(Url.getParam('file'));
		if (!isSuccess) {
			error(intl.formatMessage({id: 'apps.loggedOutCampaign.openFile.error'}), {timeout: 0, msgId: 'download-file-error'});
		}
		else {
			displayCoachMark(true, () => {}, true);
		}
		handleAnalyticsEvent({
			eventObj: {
				type:'click',
				subtype: isSuccess ? 'restart-file-download' : 'restart-file-download-fail',
				value: isSuccess ? undefined : 'invalid-path'
			}
		});
	}, [handleAnalyticsEvent, intl, displayCoachMark]);

	const getFormattedText = useCallback((messageId) => {
		if (!messageId) {
			return messageId;
		}
		let appname = product.name;
		const userName = AppsUtil.getImsData().name;
		// Remove this in future if requriements do not change.
		if ((appname == null || sapcode === 'KCCC')) {
			appname = '{firstapp}';
		}
		return formatMessage({id: messageId}, {
			appname,
			userName,
			span: str => <span className={ThorViewScss.appName}>{str}</span>,
			b: str => <b>{str}</b>
		});
	}, [formatMessage, product, sapcode]);

	const renderHeaderText = useCallback(() => (
		<div className={ThorViewScss.title}>
			<div data-cs-mask='' className={ThorViewScss.productTitle}>{getFormattedText(getHeadingTextId(WORKFLOWS.THOR, source, sapcode))}</div>
		</div>
	), [getFormattedText, sapcode, source]);

	const renderHelpSection = useCallback(() => {
		let tryAgainLinkTextId = 'apps.allAppsAppPicker.thor.tryAgainLink';
		const pathParam = Url.getParam('path');

		if (CCDUtil.isRouteToPathCampaign()
			|| (
				CCDUtil.isProductRouteToPathCampaign(sapcode)
				&& pathParam
				&& DownloadUtil.getValidUriFromUrl(pathParam)
			)) {
			return (
				<div className={ThorViewScss.helpSection}>
					<span className={ThorViewScss.tryAgainTitle}>
						{formatMessage({id: 'apps.allAppsAppPicker.thor.tryAgainTitle'})}
					</span>
					<span role='link' tabIndex={0} className={ThorViewScss.tryAgainLink} onClick={onRouteRetry}
						onKeyDown={(e) => {e.keyCode === 13 && onRouteRetry(e);}}>
						{formatMessage({id: 'apps.loggedOutCampaign.thor.routeToCCD.linkText'})}
					</span>
					<div className={ThorViewScss.reinstall}>
						<span className={ThorViewScss.reinstallTitle}>
							{formatMessage({
								id: (sapcode === 'KCCC')
									? 'apps.loggedOutCampaign.thor.routeToCCD.reinstallTitle'
									: 'apps.allAppsAppPicker.thor.reinstallTitle'
							})}
						</span>
						<a className={ThorViewScss.reinstallLink} href='#' target='download_frame' onClick={onRestartDownload}>
							{formatMessage({id: 'apps.loggedOutCampaign.thor.routeToCCD.reinstallLink'})}
						</a>
					</div>
				</div>
			);
		}
		else if (isLoggedOutCampaignExp) {
			if (isOpenFileWorkflow) {
				return (
					<div className={ThorViewScss.helpSection}>
						<span className={ThorViewScss.tryAgainTitle}>
							{formatMessage({id: 'apps.loggedOutCampaign.thor.openFile.reinstallTitle'})}
						</span>
						<span role='link' tabIndex={0} className={ThorViewScss.tryAgainLink} onClick={onOpenFile}
							onKeyDown={(e) => {e.keyCode === 13 && onOpenFile(e);}}>
							{formatMessage({id: 'apps.loggedOutCampaign.thor.openFile.reinstallLink'})}
						</span>
					</div>
				);
			}
			tryAgainLinkTextId = 'apps.common.retry';
		}
		if (sapcode === 'KCCC') {
			tryAgainLinkTextId = 'apps.allAppsAppPicker.thor.kccc.tryAgainLink';
		}
		if (AppsUtil.hasFeature('temp_wab_14286')) {
			return (
				<div className={ThorViewScss.helpSection}>
					<span className={ThorViewScss.tryAgainTitle}>
						{formatMessage({id: 'apps.allAppsAppPicker.thor.tryAgainTitle'})}
					</span>
					<span>
						{formatMessage({id: 'apps.allAppsAppPicker.retry.kaizen.helpLink'},{
							a1: str => <span role='link' tabIndex={0} className={ThorViewScss.tryAgainLink} onClick={onRetry}
								onKeyDown={(e) => {e.keyCode === 13 && onRetry(e);}}> {str} </span>,
							a2: str => <span role='link' tabIndex={0} className={ThorViewScss.helpLink} onClick={onGetHelp}
								onKeyDown={(e) => {e.keyCode === 13 && onGetHelp();}}> {str} </span>
						})}
					</span>
					<div className={ThorViewScss.reinstall}>
						<span className={ThorViewScss.reinstallTitle}>
							{formatMessage({id: 'apps.allAppsAppPicker.thor.reinstallTitle'})}
						</span>
						<a className={ThorViewScss.reinstallLink} href='#' target='download_frame' onClick={onRestartDownload}>
							{formatMessage({id: 'apps.allAppsAppPicker.thor.reinstallLink'})}
						</a>
					</div>
				</div>
			);
		}
		return (
			<div className={ThorViewScss.helpSection}>
				<span className={ThorViewScss.tryAgainTitle}>
					{formatMessage({id: 'apps.allAppsAppPicker.thor.tryAgainTitle'})}
				</span>
				<span role='link' tabIndex={0} className={ThorViewScss.tryAgainLink} onClick={onRetry}
					onKeyDown={(e) => {e.keyCode === 13 && onRetry(e);}}>
					{formatMessage({id: tryAgainLinkTextId})}
				</span>
				<span>
					{formatMessage({id: 'apps.allAppsAppPicker.kaizen.or'})}
				</span>
				<span role='link' tabIndex={0} className={ThorViewScss.helpLink} onClick={onGetHelp}
					onKeyDown={(e) => {e.keyCode === 13 && onGetHelp();}}>
					{formatMessage({id: 'apps.allAppsAppPicker.kaizen.helpLink'})}
				</span>
				<div className={ThorViewScss.reinstall}>
					<span className={ThorViewScss.reinstallTitle}>
						{formatMessage({id: 'apps.allAppsAppPicker.thor.reinstallTitle'})}
					</span>
					<a className={ThorViewScss.reinstallLink} href='#' target='download_frame' onClick={onRestartDownload}>
						{formatMessage({id: 'apps.allAppsAppPicker.thor.reinstallLink'})}
					</a>
				</div>
			</div>
		);
	}, [formatMessage, isLoggedOutCampaignExp, isOpenFileWorkflow, onGetHelp, onOpenFile, onRestartDownload, onRetry, sapcode, onRouteRetry]);

	const renderBodyText = useCallback(() => (
		<div
			className={`${ThorViewScss.body} ${isLoggedOutCampaignExp ? ThorViewScss.loggedOutCampaign : ''}`}>
			<div className={ThorViewScss.productBody}>
				{getFormattedText(getBodyTextId(WORKFLOWS.THOR, source, sapcode, true))}
			</div>
			{renderHelpSection()}
		</div>
	), [isLoggedOutCampaignExp, renderHelpSection, getFormattedText, sapcode, source]);

	const renderMessageBox = useCallback(() => {
		const messageBoxes = [];
		messagesList.forEach((message) => {
			messageBoxes.push(
				<MessageBox
					id={message.variant}
					variant={message.variant}
					onAction={message.callback}
					appDisplayName={product.name}
					intl={intl}
					key={message.variant}
					handleAnalyticsEvent = {handleAnalyticsEvent}
				/>
			);
		});
		if (messageBoxes.length !== 0) {
			return <React.Fragment> {messageBoxes}</React.Fragment>;
		}
		return false;
	}, [messagesList, handleAnalyticsEvent, product, intl]);

	const renderProductLogo = useCallback(() => {
		const productData = AppsUtil.getFFCProductConfigBySapCode(sapcode);
		const icon = AppsUtil.getProductIcon(productData);
		if ([DOWNLOAD_CONTROLLER_USER_SOURCE.LOGGED_OUT_CAMPAIGN_PAGE, DOWNLOAD_CONTROLLER_USER_SOURCE.SINGLE_APP_POST_PURCHASE_PAGE].includes(source)) {
			return icon ? (<img alt={productData.displayName} src={icon} />) : [];
		}
		return null;
	}, [sapcode, source]);

	return (
		<div className={`${ThorViewScss.thorView} ${className}`}>
			{renderProductLogo()}
			{renderHeaderText()}
			{renderBodyText()}
			{!isOpenFileWorkflow && renderMessageBox()}
		</div>
	);
};
